import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageHero } from '../components/PageHero';
import { toast } from 'sonner';
import { Users, TrendingUp, Crown, Star, Copy, ExternalLink, Settings } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SocialTradingPage() {
    const { t } = useTranslation();
    const { address } = useWeb3();
    const [influencers, setInfluencers] = useState([]);
    const [stats, setStats] = useState(null);
    const [myProfile, setMyProfile] = useState(null);
    const [followings, setFollowings] = useState([]);
    const [bio, setBio] = useState('');
    const [sort, setSort] = useState('followers');
    const [loading, setLoading] = useState(false);
    const [copyRatios, setCopyRatios] = useState({});

    const load = async () => {
        try {
            const [i, s] = await Promise.all([
                fetch(`${API}/api/social-trading/influencers?sort=${sort}&limit=50`).then(r => r.json()),
                fetch(`${API}/api/social-trading/stats`).then(r => r.json()),
            ]);
            setInfluencers(i.influencers || []);
            setStats(s);
            if (address) {
                const [p, f] = await Promise.all([
                    fetch(`${API}/api/social-trading/influencer/${address}`).then(r => r.json()),
                    fetch(`${API}/api/social-trading/followings/${address}`).then(r => r.json()),
                ]);
                setMyProfile(p.is_influencer ? p : null);
                setFollowings(f.followings || []);
            }
        } catch (e) { console.error(e); }
    };

    useEffect(() => { load(); }, [sort, address]);

    const becomeInfluencer = async () => {
        if (!address) return toast.error('Connectez votre wallet');
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/social-trading/become-influencer`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, bio, copy_fee_percent: 15 }),
            });
            const d = await res.json();
            if (d.success) { toast.success('Vous êtes maintenant un Bet Influencer'); load(); }
        } catch (e) { toast.error('Erreur'); }
        setLoading(false);
    };

    const follow = async (inf, ratio = 0.10) => {
        if (!address) return toast.error('Connectez votre wallet');
        const res = await fetch(`${API}/api/social-trading/follow`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ follower: address, influencer: inf, copy_ratio: ratio }),
        });
        const d = await res.json();
        if (d.success) { toast.success(`Vous suivez ${inf.slice(0, 10)}... (copy ratio ${(ratio * 100).toFixed(0)}%)`); load(); }
        else toast.error(d.error);
    };

    const unfollow = async (inf) => {
        await fetch(`${API}/api/social-trading/unfollow`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ follower: address, influencer: inf }),
        });
        toast.info('Désabonné');
        load();
    };

    const isFollowing = (addr) => followings.some(f => f.influencer === addr.toLowerCase());

    return (
        <div className="min-h-screen" data-testid="social-trading-page">
            <PageHero
                icon={Users}
                title={t('social.title')}
                highlight={t('social.highlight')}
                subtitle={t('social.subtitle')}
            />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">

                {/* Global stats */}
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                        <Card className="bg-white/5 border-white/10"><CardContent className="p-4"><div className="text-xs text-white/50 uppercase">Influencers</div><div className="text-2xl font-bold text-[#9B6FE8]">{stats.total_influencers}</div></CardContent></Card>
                        <Card className="bg-white/5 border-white/10"><CardContent className="p-4"><div className="text-xs text-white/50 uppercase">Abonnements</div><div className="text-2xl font-bold text-white">{stats.total_subscriptions}</div></CardContent></Card>
                        <Card className="bg-white/5 border-white/10"><CardContent className="p-4"><div className="text-xs text-white/50 uppercase">Copies totales</div><div className="text-2xl font-bold text-white">{stats.total_copies}</div></CardContent></Card>
                        <Card className="bg-[#FFD700]/5 border-[#FFD700]/20"><CardContent className="p-4"><div className="text-xs text-white/50 uppercase">Payé en B4P</div><div className="text-2xl font-bold text-[#FFD700]">{stats.total_paid_to_influencers_b4p?.toLocaleString()}</div></CardContent></Card>
                    </div>
                )}

                {/* Become Influencer */}
                <Card className="bg-gradient-to-br from-[#9B6FE8]/15 to-black border-[#9B6FE8]/30 mb-8">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <Crown className="w-5 h-5 text-[#FFD700]" />
                            {myProfile ? 'Votre profil Influencer' : 'Devenir Bet Influencer'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {myProfile ? (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                                <div><div className="text-xs text-white/50">Followers</div><div className="text-xl font-bold text-white">{myProfile.total_followers}</div></div>
                                <div><div className="text-xs text-white/50">Copies</div><div className="text-xl font-bold text-white">{myProfile.total_copies}</div></div>
                                <div><div className="text-xs text-white/50">Gagné</div><div className="text-xl font-bold text-[#FFD700]">{myProfile.total_earned_b4p?.toLocaleString()} B4P</div></div>
                                <div><div className="text-xs text-white/50">Win rate</div><div className="text-xl font-bold text-[#00FF66]">{myProfile.win_rate}%</div></div>
                            </div>
                        ) : (
                            <div className="flex flex-col sm:flex-row gap-3">
                                <Input
                                    placeholder="Votre bio (optionnel)"
                                    value={bio} onChange={e => setBio(e.target.value)}
                                    className="bg-black/30 border-white/20 text-white flex-1"
                                    data-testid="influencer-bio-input"
                                    maxLength={280}
                                />
                                <Button onClick={becomeInfluencer} disabled={loading || !address} className="bg-[#9B6FE8] text-white hover:bg-[#9B6FE8]/80" data-testid="become-influencer-btn">
                                    <Crown className="w-4 h-4 mr-2" />
                                    {loading ? '...' : 'Devenir Influencer (15%)'}
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* My Followings */}
                {address && followings.length > 0 && (
                    <Card className="bg-white/5 border-white/10 mb-8">
                        <CardHeader><CardTitle className="text-white">Mes abonnements ({followings.length})</CardTitle></CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                {followings.map(f => (
                                    <div key={f.influencer} className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-white/5" data-testid={`following-${f.influencer}`}>
                                        <div>
                                            <div className="text-white font-mono text-sm">{f.influencer.slice(0, 10)}...{f.influencer.slice(-4)}</div>
                                            <div className="text-xs text-white/40">Copy ratio: {(f.copy_ratio * 100).toFixed(0)}% · Win rate: {f.influencer_profile?.win_rate}%</div>
                                        </div>
                                        <Button onClick={() => unfollow(f.influencer)} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                                            Unfollow
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Influencers Browser */}
                <div className="flex items-center gap-3 mb-4">
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2"><Star className="w-5 h-5 text-[#FFD700]" />Top Influencers</h2>
                    <div className="flex-1" />
                    <select value={sort} onChange={e => setSort(e.target.value)} className="bg-black/30 border border-white/20 text-white rounded-lg px-3 py-2 text-sm" data-testid="influencer-sort">
                        <option value="followers">+ de followers</option>
                        <option value="earnings">+ gagnant</option>
                        <option value="copies">+ copié</option>
                        <option value="recent">Plus récents</option>
                    </select>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    {influencers.length === 0 ? (
                        <Card className="md:col-span-2 bg-white/5 border-white/10"><CardContent className="p-8 text-center text-white/40">Aucun influencer pour le moment. Soyez le premier !</CardContent></Card>
                    ) : influencers.map(inf => (
                        <Card key={inf.address} className="bg-white/5 border-white/10 hover:border-[#9B6FE8]/30 transition-all" data-testid={`influencer-${inf.address}`}>
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#9B6FE8] to-[#00FFFF] flex items-center justify-center text-white font-bold">
                                                {inf.address.slice(2, 4).toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="text-white font-mono text-sm">{inf.address.slice(0, 10)}...{inf.address.slice(-4)}</div>
                                                <div className="flex gap-2 mt-0.5">
                                                    <Badge className="bg-[#9B6FE8]/10 text-[#9B6FE8] border-[#9B6FE8]/30 text-xs">{inf.copy_fee_percent}% fee</Badge>
                                                    {inf.win_rate >= 60 && <Badge className="bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/30 text-xs">Hot</Badge>}
                                                </div>
                                            </div>
                                        </div>
                                        {inf.bio && <div className="text-white/60 text-xs mt-2 max-w-xs">{inf.bio}</div>}
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 gap-2 mb-3 text-center">
                                    <div><div className="text-[#FFD700] font-bold text-lg">{inf.total_followers}</div><div className="text-[10px] text-white/40 uppercase">Followers</div></div>
                                    <div><div className="text-white font-bold text-lg">{inf.resolved_bets || 0}</div><div className="text-[10px] text-white/40 uppercase">Paris</div></div>
                                    <div><div className="text-[#00FF66] font-bold text-lg">{inf.win_rate}%</div><div className="text-[10px] text-white/40 uppercase">Win</div></div>
                                    <div><div className={`font-bold text-lg ${inf.roi_percent >= 0 ? 'text-[#00FF66]' : 'text-red-400'}`}>{inf.roi_percent >= 0 ? '+' : ''}{inf.roi_percent}%</div><div className="text-[10px] text-white/40 uppercase">ROI</div></div>
                                </div>
                                <div className="flex gap-2">
                                    <Input
                                        type="number" step="0.05" min="0.01" max="1"
                                        placeholder="0.10"
                                        value={copyRatios[inf.address] ?? ''}
                                        onChange={e => setCopyRatios(c => ({ ...c, [inf.address]: e.target.value }))}
                                        className="bg-black/30 border-white/20 text-white h-9 w-24"
                                        data-testid={`copy-ratio-${inf.address}`}
                                    />
                                    {isFollowing(inf.address) ? (
                                        <Button onClick={() => unfollow(inf.address)} variant="outline" className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`unfollow-${inf.address}`}>
                                            Désabonner
                                        </Button>
                                    ) : (
                                        <Button onClick={() => follow(inf.address, parseFloat(copyRatios[inf.address]) || 0.10)} disabled={address?.toLowerCase() === inf.address} className="flex-1 bg-[#9B6FE8] text-white hover:bg-[#9B6FE8]/80" data-testid={`follow-${inf.address}`}>
                                            <Copy className="w-4 h-4 mr-1" />Suivre & Copier
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                <div className="mt-8 p-4 bg-white/5 rounded-lg border border-white/10 text-sm text-white/60 flex items-start gap-3">
                    <TrendingUp className="w-5 h-5 text-[#9B6FE8] mt-0.5 flex-shrink-0" />
                    <div>
                        <strong className="text-white">Comment ça marche :</strong> Chaque fois qu'un follower copie un pari d'un influencer et gagne, l'influencer touche automatiquement <strong className="text-[#FFD700]">15% du profit du follower</strong> en B4P. La plateforme prélève en plus <strong>2%</strong> de buy-back. Tout est 100% automatisé : le follower copie en 1 clic, l'influencer gagne en dormant.
                    </div>
                </div>
            </div>
        </div>
    );
}
